import equal from 'fast-deep-equal/es6/react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import Input, { type ISliderInputProps } from './Input';
import s from './SliderWithTooltip.module.scss';

interface IUseTooltipArgs {
  value: number | null | undefined;
  min: number;
  max: number;
  tooltipWidth: number | null;
}

interface ITooltipProps extends Omit<ISliderInputProps, 'value' | 'setValue'> {
  numberInputValue: number | null | undefined;
  setNumberInputValue: ISliderInputProps['setValue'];
  setStepValue: (step: number) => void;
}

// The arithmetic reads a missing value like the former code did (null as 0, undefined as NaN);
// the width is null before the first measurement (`-nullpx` beyond 70%).
const useTooltip = ({ value, min, max, tooltipWidth }: IUseTooltipArgs) => {
  const stylesForTooltip = useMemo(() => {
    let marginLeft = '0px';
    let percentage = ((Number(value) - min) / (max - min)) * 100;

    if (percentage > 30 && percentage <= 70) {
      marginLeft = `-${Number(tooltipWidth) / 2}px`;
    } else if (percentage > 70) {
      marginLeft = `-${tooltipWidth}px`;
    } else {
      percentage *= 0.94;
    }

    const styles = { left: `${percentage}%`, marginLeft };
    return styles;
  }, [min, max, value, tooltipWidth]);

  const stylesForTriangle = useMemo(() => {
    const percentage = ((Number(value) - min) / (max - min)) * 100;
    let left = '10%';

    if (percentage > 30 && percentage <= 70) {
      left = '50%';
    } else if (percentage > 70) {
      left = '90%';
    }

    return { left };
  }, [value, min, max]);

  return { stylesForTriangle, stylesForTooltip };
};

const Tooltip = ({
  sliderValue,
  min,
  max,
  numberInputValue,
  setNumberInputValue,
  ...rest
}: ITooltipProps) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipWidth, setTooltipWidth] = useState<number | null>(null);
  const { stylesForTooltip, stylesForTriangle } = useTooltip({
    value: sliderValue,
    min,
    max,
    tooltipWidth,
  });

  useEffect(() => {
    if (!tooltipWidth && tooltipRef?.current) {
      setTooltipWidth(tooltipRef?.current.clientWidth);
    }
  }, [tooltipWidth]);

  return (
    <div className={s.tooltip} style={stylesForTooltip} ref={tooltipRef}>
      <Input
        min={min}
        max={max}
        sliderValue={sliderValue}
        {...rest}
        value={numberInputValue}
        setValue={setNumberInputValue}
      />
      <span className={s.triangle} style={stylesForTriangle} />
    </div>
  );
};

export default memo(Tooltip, equal);
